import type { resourceEventHistoryApiSpec } from "@wingspanhq/wingspan-api-types";
import { EventGroup, GROUP_WINDOW_MS } from "./types";

const sortByTimestampDescending = (
  events: resourceEventHistoryApiSpec.IResourceEvent[]
): resourceEventHistoryApiSpec.IResourceEvent[] =>
  [...events].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

const getGroupingKey = (
  event: resourceEventHistoryApiSpec.IResourceEvent
): string => `${event.userName ?? ""}::${event.type ?? ""}`;

const isWithinGroupWindow = (
  anchorTime: number,
  candidateTime: number
): boolean => Math.abs(anchorTime - candidateTime) <= GROUP_WINDOW_MS;

const toEventGroup = (
  events: resourceEventHistoryApiSpec.IResourceEvent[]
): EventGroup => {
  const anchor = events[0];

  if (events.length === 1) {
    return { type: "single", event: anchor };
  }

  return {
    type: "grouped",
    events,
    actor: anchor.userName || "Automated",
    eventType: anchor.type || "",
    eventTitle: anchor.title,
    anchorTimestamp: new Date(anchor.timestamp)
  };
};

/**
 * Groups events by actor (userName), event type, and time window.
 * Events are grouped when they share the same actor and type,
 * and occur within 30 minutes of the first (anchor) event in the group.
 */
export function groupEvents(
  events: resourceEventHistoryApiSpec.IResourceEvent[]
): EventGroup[] {
  if (events.length === 0) return [];

  const sorted = sortByTimestampDescending(events);
  const processed = new Set<number>();

  return sorted.reduce<EventGroup[]>((groups, anchor, anchorIndex) => {
    if (processed.has(anchorIndex)) return groups;
    processed.add(anchorIndex);

    if (!anchor.type) {
      return [...groups, { type: "single", event: anchor }];
    }

    const anchorKey = getGroupingKey(anchor);
    const anchorTime = new Date(anchor.timestamp).getTime();

    const groupedEvents = sorted.reduce<
      resourceEventHistoryApiSpec.IResourceEvent[]
    >(
      (matched, candidate, candidateIndex) => {
        if (candidateIndex <= anchorIndex || processed.has(candidateIndex))
          return matched;

        if (
          getGroupingKey(candidate) === anchorKey &&
          isWithinGroupWindow(
            anchorTime,
            new Date(candidate.timestamp).getTime()
          )
        ) {
          processed.add(candidateIndex);
          return [...matched, candidate];
        }

        return matched;
      },
      [anchor]
    );

    return [...groups, toEventGroup(groupedEvents)];
  }, []);
}
